import { forEach as _forEach } from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import { Constants } from '@pwc/pwc-deals-web-common';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Icon, PopoverMenu, Button } from '@pwc/pwc-deals-ui';
import { WORKFLOW_ACTIONS } from 'common/hooks/useApprovalWorkflow';
import { getWorkflowLoading } from 'common/redux/workflow/workflowSlice';
import EnableForProjectState from 'features/security/EnableForProjectState';
import { useTranslation } from 'react-i18next';
import {
  getErrorMoreMenuState,
  hideMoreMenuError,
} from '../../redux/errors/errorsSlice';
import LiveLinkingIndicator from './LiveLinkingIndicator/LiveLinkingIndicator';

const hideOverlay = (callback, showPopup) => () => {
  showPopup(false);
  if (callback) {
    callback();
  }
};

const inProgressActionName = 'in-progress-version';
const clientVersionActionName = 'client-version';

const MoreMenu = ({
  itemId,
  onDelete,
  onRefresh,
  onEdit,
  viewClient,
  workflowActions,
  reportPreviewUrl,
  isTopicHeader,
  isDisabled = false,
  isEditDisabled = false,
  isMobile = false,
}) => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const history = useHistory();
  const { projectId } = useParams();
  const isOnClientVersion = viewClient && viewClient.isOnClientVersion;
  const moreMenuError = useSelector(getErrorMoreMenuState);
  const workflowLoading = useSelector(getWorkflowLoading);
  const [actionToConfirm, setActionToConfirm] = useState();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const showPopup = useCallback(
    (isShown) => {
      setIsPopupVisible(isShown);
      if (!isShown) {
        setActionToConfirm(undefined);
      }
    },
    [setIsPopupVisible, setActionToConfirm]
  );

  useEffect(() => {
    if (
      moreMenuError &&
      moreMenuError.itemId === itemId &&
      moreMenuError.show
    ) {
      showPopup(true);
    }
  }, [itemId, moreMenuError, showPopup]);

  const limitedWorkflowActions = (workflowActions || []).filter((action) => {
    return action.action !== WORKFLOW_ACTIONS.SHARE_WITH_THIRD_PARTY;
  });

  const workflowActionsWithIcons = (limitedWorkflowActions || []).map(
    (action) => ({
      ...action,
      update:
        action.action === WORKFLOW_ACTIONS.SHARE
          ? () => {
              showPopup(false);
              action.update();
            }
          : action.update,
      disabled: isOnClientVersion || isDisabled,
      icon: (
        <Icon
          name={action.icon}
          width="21"
          className="sr2 outline icon-orange"
        />
      ),
    })
  );

  let actions = [];

  if (onEdit) {
    actions.push({
      name: 'edit',
      action: 'edit',
      text: t('analysisMenuEdit'),
      update: hideOverlay(onEdit, showPopup),
      icon: (
        <Icon name="pencil" width="21" className="sr2 outline icon-orange" />
      ),
      disabled: isDisabled || isEditDisabled || isMobile,
    });
  }

  if (onRefresh) {
    actions.push({
      name: 'refresh',
      action: 'refresh',
      text: t('refreshData'),
      update: hideOverlay(onRefresh, showPopup),
      confirmation: undefined,
      icon: (
        <Icon name="refresh" width="21" className="sr2 outline icon-orange" />
      ),
      disabled: isOnClientVersion || isDisabled,
    });
  }

  actions = actions.concat(workflowActionsWithIcons);

  if (reportPreviewUrl) {
    actions.push({
      action: 'Preview Report',
      name: 'preview-report',
      text: t('previewReport'),
      update: () => {
        history.push(reportPreviewUrl);
      },
      icon: (
        <Icon name="report" width="21" className="sr2 outline icon-orange" />
      ),
      disabled: isDisabled,
    });
  }

  actions.push({
    name: 'delete',
    action: 'delete',
    text: t('analysisMenuDelete'),
    update: hideOverlay(onDelete, showPopup),
    icon: <Icon name="bin" width="21" className="sr2 outline icon-orange" />,
    disabled: isOnClientVersion || isDisabled,
  });

  const onActionClick = (action) => {
    if (action.confirmation) {
      return () => {
        setActionToConfirm(action);
      };
    }
    return action.update;
  };

  const cancelActionToConfirm = () => {
    setActionToConfirm(undefined);
  };

  const confirmActionToConfirm = () => {
    actionToConfirm.update();
    setActionToConfirm(undefined);
  };

  const closeErrorState = () => {
    dispatch(hideMoreMenuError());
    cancelActionToConfirm();
  };

  const RenderContent = () => {
    if (moreMenuError && moreMenuError.itemId === itemId) {
      let title = 'unableToSave';
      let message = 'systemError';
      if (
        moreMenuError.moreMenuErrorType ===
        Constants.MoreMenuErrorType.LIBRARY_ITEM_CORRUPTED
      ) {
        title = 'cantShareAnalysis';
        message = 'contentMissing';
      }

      return (
        <PopoverMenu
          placement="bottom-end"
          id="popover-moremenu"
          data-testid="popover-moremenu"
          isButtonSecondary
          popoverTitle={t(title)}
          show={isPopupVisible}
          onToggle={(nextShow) => {
            showPopup(nextShow);
          }}
        >
          <p
            data-testid="popover-confirmation-error-message"
            className="popup-description"
          >
            {t(message)}
          </p>
          <div className="confirm-button-error-state">
            <Button
              data-testid="popover-confirmation-error-cancel"
              onClick={closeErrorState}
              className="button-link mr-3"
            >
              {t('cancelKFA')}
            </Button>
          </div>
        </PopoverMenu>
      );
    }
    if (!actionToConfirm) {
      return (
        <PopoverMenu
          placement="bottom-end"
          id="popover-moremenu"
          data-testid="popover-moremenu"
          isButtonSecondary={isTopicHeader}
          show={isPopupVisible}
          onToggle={(nextShow) => {
            showPopup(nextShow);
          }}
        >
          {actions.map((action) => (
            <Button
              key={action.action}
              className="button-menu"
              onClick={onActionClick(action)}
              data-testid={`menu-button-${action.name}`}
              disabled={action.disabled || workflowLoading}
            >
              {action.icon ? action.icon : null}
              {action.text}
            </Button>
          ))}
        </PopoverMenu>
      );
    }

    return (
      <PopoverMenu
        placement="bottom-end"
        id="popover-moremenu"
        data-testid="popover-moremenu"
        isButtonSecondary
        popoverTitle={actionToConfirm.confirmation.title}
        show={isPopupVisible}
        onToggle={(nextShow) => {
          showPopup(nextShow);
        }}
      >
        <p data-testid="popover-confirmation-message">
          {actionToConfirm.confirmation.message}
        </p>
        {actionToConfirm.confirmation.customContent === 'liveLinking' && (
          <LiveLinkingIndicator />
        )}
        <div className="confirm-buttons">
          <Button
            data-testid="popover-confirmation-cancel"
            onClick={cancelActionToConfirm}
            className="button-link mr-3"
          >
            {t('cancelKeyFocusArea')}
          </Button>
          <Button
            data-testid="popover-confirmation-confirm"
            onClick={confirmActionToConfirm}
            className="button-small"
          >
            {actionToConfirm.confirmation.confirmButtonText}
          </Button>
        </div>
      </PopoverMenu>
    );
  };

  return (
    <EnableForProjectState projectId={projectId}>
      {({ isProjectDisabled }) => {
        if (isProjectDisabled) {
          _forEach(actions, (action) => {
            action.disabled = [
              inProgressActionName,
              clientVersionActionName,
            ].includes(action.name)
              ? action.disabled
              : true;
          });
        }

        return RenderContent();
      }}
    </EnableForProjectState>
  );
};

export default MoreMenu;
